// frontend/src/hooks/usePermission.js
import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import permissionService from '../services/permissionService';

/**
 * Hook personnalisé pour vérifier si l'utilisateur a une permission spécifique
 * @param {string} permissionName - Nom de la permission à vérifier
 * @returns {Object} { hasPermission, loading, error }
 */
export const usePermission = (permissionName) => {
  const { user, isAuthenticated } = useAuth();
  const isAdminUser = Boolean(user?.isAdmin);
  const [hasPermission, setHasPermission] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  useEffect(() => {
    const checkPermission = async () => {
      if (!isAuthenticated || !permissionName) {
        setHasPermission(false);
        setLoading(false);
        return;
      }
      
      try {
        // Si l'utilisateur est super admin, il a toutes les permissions
        if (isAdminUser) {
          setHasPermission(true);
          setLoading(false);
          return;
        }
        
        // Vérifier la permission spécifique
        const result = await permissionService.checkPermission(permissionName);
        setHasPermission(result);
      } catch (error) {
        console.error(`Error checking permission ${permissionName}:`, error);
        setError(error);
        setHasPermission(false);
      } finally {
        setLoading(false);
      }
    };
    
    checkPermission();
  }, [isAuthenticated, isAdminUser, permissionName]);
  
  return { hasPermission, loading, error };
};

// Exporter le hook comme export par défaut pour faciliter l'importation
export default usePermission;